import type { ElementType } from "react";
import { Link } from "react-router-dom";
import { Route } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ScenarioRowProps } from "./scenario-row-types";

export function ScenarioVariantCompact({
  scenario,
  effectiveHref,
  onSelect,
  actions,
  trailing,
  leading,
  badge,
  subtitle,
  headingLevel: Heading = "h3",
  className,
}: Pick<
  ScenarioRowProps,
  | "scenario"
  | "onSelect"
  | "actions"
  | "trailing"
  | "leading"
  | "badge"
  | "subtitle"
  | "className"
> & {
  effectiveHref: string | null;
  headingLevel?: ElementType;
}) {
  const descriptionText = subtitle ?? scenario.description;
  return (
    <div
      className={cn(
        "group flex min-w-0 flex-1 items-center justify-between gap-3 px-3 py-2 transition-colors hover:bg-muted/20",
        className,
      )}
    >
      <div className="flex min-w-0 items-center gap-2.5">
        {leading ?? (
          <Route
            className="size-4 shrink-0 text-muted-foreground"
            aria-hidden="true"
          />
        )}
        <div className="min-w-0 flex-1 space-y-0.5">
          <Heading
            className="min-w-0 truncate text-sm font-semibold leading-tight text-foreground"
            title={scenario.name}
          >
            {effectiveHref ? (
              <Link
                to={effectiveHref}
                aria-label={`View scenario: ${scenario.name}`}
                onClick={(e) => {
                  if (
                    onSelect &&
                    !e.metaKey &&
                    !e.ctrlKey &&
                    !e.shiftKey &&
                    !e.altKey &&
                    e.button === 0
                  ) {
                    e.preventDefault();
                    onSelect(scenario.id);
                  }
                }}
                className="hover:underline underline-offset-4"
              >
                {scenario.name}
              </Link>
            ) : onSelect ? (
              <button
                type="button"
                onClick={() => onSelect(scenario.id)}
                className="cursor-pointer text-left hover:underline underline-offset-4"
              >
                {scenario.name}
              </button>
            ) : (
              <span>{scenario.name}</span>
            )}
          </Heading>
          {descriptionText && (
            <p
              className="line-clamp-1 text-xs text-muted-foreground"
              title={
                typeof descriptionText === "string"
                  ? descriptionText
                  : undefined
              }
            >
              {descriptionText}
            </p>
          )}
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {badge}
        {trailing}
        {actions}
      </div>
    </div>
  );
}
